import {Subject} from "rxjs";
import {User} from "../app/user";
import assert from "assert";
import {uniqueKey, valueOrEmptyObject} from "../utils/tools";
import {EinzelBuchung, Person, Rechnung, Reise} from "../app/types";

export class GruppenBuchung {
    buchungsListeChanged = new Subject();
    rechnungChanged = new Subject();

    readonly bucher: User = null;
    private _reisen: Reise[] = [];
    private weitereTeilnehmer: Person[] = [];
    private _alleTeilnehmer: Person[] = [];
    private einzelBuchungen = {};

    constructor(bucher: User) {
        assert(bucher !== null);
        this.bucher = bucher;
        this.updateAlleTeilnehmer();
        this.addBuchungenForPerson(bucher);
        this.buchungsListeChanged.subscribe(() => this.rechnungChanged.next(null));
        this.bucher.rechnung.subscribe(() => this.updateBereitsGebuchtFlags());
    }

    // valid if at least one buchung is selected and all required values are set in all selected einzelBuchungen
    get valid(): boolean {
        let atLeastOneSelected = false;
        let allSelectedAreValid = true;
        Object.keys(valueOrEmptyObject(this.einzelBuchungen)).forEach(key => {
            const buchung: EinzelBuchung = this.einzelBuchungen[key];
            if (buchung.selected) {
                atLeastOneSelected = true;
                if (!buchung.valid) {
                    allSelectedAreValid = false;
                }
            }
        });
        return atLeastOneSelected && allSelectedAreValid;
    }

    get reisen() {
        return this._reisen;
    }
    addReise(reise: Reise) {
        this._reisen.push(reise);
        this._alleTeilnehmer.forEach(person => {
            const key = this.key(reise, person);
            this.einzelBuchungen[key] = this.setBereitsGebuchtFlag(new EinzelBuchung(key, null, person, reise));
        });
        this.buchungsListeChanged.next(null);
    }
    removeReise(reise: Reise) {
        this._reisen = this._reisen.filter(x => x !== reise);
        this._alleTeilnehmer.forEach(person => {
            delete this.einzelBuchungen[this.key(reise, person)];
        });
        this.buchungsListeChanged.next(null);
    }

    get alleTeilnehmer() {
        return this._alleTeilnehmer;
    }
    createWeiterePerson(): Person {
        assert(this.bucher !== null);
        return new Person(`${this.bucher.uid}_${uniqueKey()}`);
    }
    addWeiterePerson(person: Person) {
        const prevPerson = this.weitereTeilnehmer.find(p => p.$key === person.$key);
        if (prevPerson) {
            Object.assign(prevPerson, person);
            this.buchungsListeChanged.next(null);
            return;
        }
        this.weitereTeilnehmer.push(person);
        this.updateAlleTeilnehmer();
        this.addBuchungenForPerson(person);
        this.buchungsListeChanged.next(null);
    }
    removeWeiterePerson(person: Person) {
        person = this.weitereTeilnehmer.find(p => p.$key === person.$key);
        if (!person) {
            return;
        }
        this.weitereTeilnehmer = this.weitereTeilnehmer.filter(p => p !== person);
        this.updateAlleTeilnehmer();
        this.removeBuchungenForPerson(person);
        this.buchungsListeChanged.next(null);
    }
    private addBuchungenForPerson(person: Person) {
        this._reisen.forEach(reise => {
            const key = this.key(reise, person);
            this.einzelBuchungen[key] = this.setBereitsGebuchtFlag(new EinzelBuchung(key, null, person, reise));
        });
    }
    private removeBuchungenForPerson(person: Person) {
        this._reisen.forEach(reise => {
            delete this.einzelBuchungen[this.key(reise, person)];
        });
    }
    private updateAlleTeilnehmer() {
        this._alleTeilnehmer = [this.bucher as Person].concat(this.weitereTeilnehmer);
    }

    get(reise: Reise, teilnehmer: Person): EinzelBuchung {
        const key = this.key(reise, teilnehmer);
        const buchung = this.einzelBuchungen[key];
        assert(buchung != null, 'EinzelBuchung missing!');
        assert(buchung.reise != null, `EinzelBuchung broken: ${buchung}`);
        return buchung;
    }

    set(data: any) {
        const key = this.key(data.reise, data.teilnehmer);
        const buchung: EinzelBuchung = this.einzelBuchungen[key];
        assert(buchung != null, 'EinzelBuchung missing!');
        buchung.selected = data.selected;
        buchung.zustieg = data.zustieg;
        buchung.kategorie = data.kategorie;
        buchung.kurs = data.kurs;
        buchung.zimmerwunsch = data.zimmerwunsch;
        buchung.kurtaxe = data.kurtaxe;
        buchung.sorgenfreiTarif = data.sorgenfreiTarif;
        buchung.gesamtBetrag = buchung.defaultBetrag;
        this.rechnungChanged.next(null);
    }

    get validBuchungen(): EinzelBuchung[] {
        const validBuchungen: EinzelBuchung[] = [];
        Object.keys(valueOrEmptyObject(this.einzelBuchungen)).forEach(key => {
            const buchung: EinzelBuchung = this.einzelBuchungen[key];
            if (buchung.selected && buchung.valid) {
                validBuchungen.push(buchung);
            }
        });
        return validBuchungen;
    }

    toJson() {
        const json = {
            bucherUid: this.bucher.uid,
            buchungen: this.validBuchungen.map(buchung => {
                buchung.bucher = this.bucher;
                return buchung.toJson();
            }),
            weitereTeilnehmer: {}
        };
        this.weitereTeilnehmer.forEach(teilnehmer => json.weitereTeilnehmer[teilnehmer.$key] = teilnehmer.toJson());
        return json;
    }

    get rechnung(): Rechnung {
        return new Rechnung(this.validBuchungen);
    }

    checkStatus(status) {
        this.validBuchungen.forEach(eb => {
            if (eb._status !== status) {
                throw new Error(`EinzelBuchung ${eb.$key} hat unerwarteten Status: act=${eb.status} exp=${status}`);
            }
        });
    }

    private setBereitsGebuchtFlag(newEb: EinzelBuchung): EinzelBuchung {
        const rechnung = this.bucher.rechnung.getValue();
        if (!rechnung) {
            return newEb;
        }
        const oldEb = rechnung.alleItems.buchungen.find(ebOld =>
            ebOld.reise.$key === newEb.reise.$key && ebOld.teilnehmer.name() === newEb.teilnehmer.name());
        newEb.bereitsGebucht = oldEb != null;
        return newEb;
    }

    private updateBereitsGebuchtFlags() {
        Object.keys(valueOrEmptyObject(this.einzelBuchungen))
            .forEach(key => this.setBereitsGebuchtFlag(this.einzelBuchungen[key]));
    }

    private key(reise: Reise, teilnehmer: Person) {
        return reise.$key + '__' + teilnehmer.$key;
    }
}
